// ============================================
// COMPANIES LIST PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  MoreVertical,
  Eye,
  Edit,
  ShieldCheck,
  Globe,
  MapPin,
  Star,
  Ship,
  Ban,
  CheckCircle,
} from 'lucide-react';
import type { Company } from '@crew-at-sea/shared-types';
import {
  PageHeader,
  DataTable,
  FilterBar,
  FilterSelect,
  VerificationBadge,
  ConfirmDialog,
} from '@/components';
import { adminApi } from '@/services/api';
import { formatDate, formatNumber, cn } from '@/utils';

export const Route = createFileRoute('/_authenticated/companies/')({
  component: CompaniesPage,
});

function CompaniesPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    company: Company | null;
    action: 'verify' | 'suspend' | 'activate';
  }>({ open: false, company: null, action: 'verify' });

  // Şirketleri getir
  const { data: rawData, isLoading } = useQuery({
    queryKey: ['admin', 'companies', { page, limit, search }],
    queryFn: async () => {
      const response = await adminApi.getCompanies({ page, limit, search: search || undefined });
      return (response as any)?.data || response;
    },
  });

  // Veri yapısını normalize et
  const data = rawData ? {
    data: rawData.data || [],
    meta: rawData.meta || { total: 0, page: 1, limit: 10, totalPages: 1, hasMore: false }
  } : { data: [], meta: { total: 0, page: 1, limit: 10, totalPages: 1, hasMore: false } };

  // Doğrulama mutation
  const verifyMutation = useMutation({
    mutationFn: (id: string) => adminApi.updateCompany(id, { isVerified: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      setConfirmDialog({ open: false, company: null, action: 'verify' });
    },
  });

  // Askıya Al/Aktifleştir mutation
  const statusMutation = useMutation({
    mutationFn: ({ userId, status, reason }: { userId: string; status: string; reason?: string }) =>
      adminApi.updateUserStatus(userId, { status: status as any, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      setConfirmDialog({ open: false, company: null, action: 'verify' });
    },
  });

  const handleConfirmAction = (reason?: string) => {
    if (!confirmDialog.company) return;
    
    if (confirmDialog.action === 'verify') {
      verifyMutation.mutate(confirmDialog.company.id);
    } else if (confirmDialog.action === 'suspend' || confirmDialog.action === 'activate') {
      const newStatus = confirmDialog.action === 'suspend' ? 'SUSPENDED' : 'ACTIVE';
      statusMutation.mutate({ userId: confirmDialog.company.userId, status: newStatus, reason });
    }
  };

  const columns = [
    {
      key: 'company',
      header: 'Şirket',
      render: (company: Company) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-maritime bg-maritime-100 text-maritime-700 font-bold text-lg">
            {company.logoUrl ? (
              <img
                src={company.logoUrl}
                alt={company.name}
                className="w-full h-full object-cover rounded-maritime"
              />
            ) : (
              company.name.charAt(0)
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900">{company.name}</p>
              {company.isVerified && (
                <ShieldCheck className="h-4 w-4 text-green-500" />
              )}
            </div>
            {company.website && (
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-maritime-600"
              >
                <Globe className="h-3 w-3" />
                {new URL(company.website).hostname}
              </a>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Konum',
      render: (company: Company) => (
        <div className="flex items-center gap-1 text-gray-600">
          <MapPin className="h-4 w-4 text-gray-400" />
          <span>
            {company.city && `${company.city}, `}
            {company.country}
          </span>
        </div>
      ),
    },
    {
      key: 'fleet',
      header: 'Filo',
      render: (company: Company) => (
        <div className="flex items-center gap-1 text-gray-600">
          <Ship className="h-4 w-4 text-gray-400" />
          <span>{company.fleetSize || '-'} gemi</span>
        </div>
      ),
    },
    {
      key: 'rating',
      header: 'Değerlendirme',
      render: (company: Company) => (
        <div className="flex items-center gap-1">
          <Star className="h-4 w-4 text-amber-400 fill-current" />
          <span className="font-medium">{company.averageRating.toFixed(1)}</span>
          <span className="text-gray-400 text-sm">({company.reviewCount})</span>
        </div>
      ),
    },
    {
      key: 'jobs',
      header: 'İlanlar',
      render: (company: Company) => (
        <span className="text-gray-600">{formatNumber(company.totalJobsPosted)}</span>
      ),
    },
    {
      key: 'verified',
      header: 'Doğrulama',
      render: (company: Company) => (
        <VerificationBadge isVerified={company.isVerified} />
      ),
    },
    {
      key: 'createdAt',
      header: 'Kayıt',
      render: (company: Company) => (
        <span className="text-gray-500">{formatDate(company.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (company: Company) => (
        <div className="relative group">
          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
            <MoreVertical className="h-4 w-4" />
          </button>
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-maritime shadow-maritime-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            <div className="py-1">
              <Link
                to={`/companies/${company.id}` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Eye className="h-4 w-4" />
                Görüntüle
              </Link>
              <Link
                to={`/companies/${company.id}/edit` as any}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Edit className="h-4 w-4" />
                Düzenle
              </Link>
              <hr className="my-1" />
              {!company.isVerified && (
                <button 
                  onClick={() => setConfirmDialog({ open: true, company, action: 'verify' })}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 w-full"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Doğrula
                </button>
              )}
              {(company as any).user?.status === 'SUSPENDED' ? (
                <button 
                  onClick={() => setConfirmDialog({ open: true, company, action: 'activate' })}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-green-50 w-full"
                >
                  <CheckCircle className="h-4 w-4" />
                  Aktifleştir
                </button>
              ) : (
                <button 
                  onClick={() => setConfirmDialog({ open: true, company, action: 'suspend' })}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 w-full"
                >
                  <Ban className="h-4 w-4" />
                  Askıya Al
                </button>
              )}
            </div>
          </div>
        </div>
      ),
    },
  ];

  const verifiedOptions = [
    { value: 'true', label: 'Doğrulanmış' },
    { value: 'false', label: 'Doğrulanmamış' },
  ];

  const activeFiltersCount = (verifiedFilter ? 1 : 0) + (search ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Şirketler"
        description="Kayıtlı şirketleri yönetin"
      />

      <FilterBar
        searchValue={search}
        onSearchChange={(val) => { setSearch(val); setPage(1); }}
        searchPlaceholder="Şirket adı ile ara..."
        filters={
          <FilterSelect
            value={verifiedFilter}
            onChange={setVerifiedFilter}
            options={verifiedOptions}
            placeholder="Doğrulama Durumu"
          />
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setSearch('');
          setVerifiedFilter('');
        }}
      />

      <DataTable
        columns={columns}
        data={data?.data || []}
        keyExtractor={(company) => company.id}
        loading={isLoading}
        emptyMessage="Şirket bulunamadı"
        pagination={{
          page,
          limit,
          total: data?.meta.total || 0,
          totalPages: data?.meta.totalPages || 1,
          onPageChange: setPage,
          onLimitChange: setLimit,
        }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog({ ...confirmDialog, open })
        }
        title={
          confirmDialog.action === 'verify'
            ? 'Şirketi Doğrula'
            : confirmDialog.action === 'suspend'
            ? 'Şirketi Askıya Al'
            : 'Şirketi Aktifleştir'
        }
        description={
          confirmDialog.action === 'verify'
            ? `${confirmDialog.company?.name} şirketini doğrulamak istediğinize emin misiniz?`
            : confirmDialog.action === 'suspend'
            ? `${confirmDialog.company?.name} şirketini askıya almak istediğinize emin misiniz? Tüm ilanları ve başvuruları pasif olacak.`
            : `${confirmDialog.company?.name} şirketini aktifleştirmek istediğinize emin misiniz?`
        }
        variant={confirmDialog.action === 'suspend' ? 'warning' : 'success'}
        confirmText={
          confirmDialog.action === 'verify'
            ? 'Doğrula'
            : confirmDialog.action === 'suspend'
            ? 'Askıya Al'
            : 'Aktifleştir'
        }
        onConfirm={handleConfirmAction}
        loading={verifyMutation.isPending || statusMutation.isPending}
        showReasonInput={confirmDialog.action === 'suspend'}
        reasonPlaceholder="Askıya alma sebebi (opsiyonel)"
      />
    </div>
  );
}
